import {
  Button,
  Flex,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { closeModal, openModal } from '@mantine/modals';
import { MODAL_SIZE_MEDIUM } from '@shared/ui';
import { $VacationGenerateSchema } from './types';
import {
  useGenerateVacancy,
  type GenerateVacancyResponse,
} from '@entities/vacancy';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useNavigate } from 'react-router';
import { INITIAL_VALUES } from '../../constants';

const SpinnerIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='72'
    height='72'
    viewBox='0 0 72 72'
    fill='none'
    style={{ animation: 'spin 1s linear infinite' }}
  >
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    <path
      d='M67.3459 35.7339C69.9163 35.7339 72.031 37.8261 71.6996 40.3751C70.9878 45.8498 69.0232 51.1095 65.9329 55.7344C61.9772 61.6546 56.3547 66.2688 49.7766 68.9936C43.1985 71.7183 35.9601 72.4312 28.9767 71.0422C21.9934 69.6531 15.5788 66.2244 10.5442 61.1897C5.50947 56.155 2.0808 49.7405 0.69173 42.7571C-0.697338 35.7738 0.0155821 28.5354 2.74034 21.9573C5.46509 15.3791 10.0793 9.75671 15.9995 5.80098C20.6244 2.71071 25.8841 0.746046 31.3588 0.0342868C33.9077 -0.29709 36 1.81758 36 4.38795C36 6.95832 33.9008 9.0008 31.3694 9.44673C27.7392 10.0862 24.2636 11.4739 21.1708 13.5404C16.7813 16.4733 13.3602 20.642 11.3399 25.5193C9.31968 30.3967 8.79109 35.7635 9.821 40.9412C10.8509 46.1189 13.3931 50.875 17.126 54.6079C20.8589 58.3408 25.615 60.883 30.7927 61.9129C35.9704 62.9428 41.3372 62.4142 46.2145 60.394C51.0918 58.3737 55.2605 54.9526 58.1935 50.5631C60.26 47.4703 61.6477 43.9947 62.2872 40.3645C62.7331 37.8331 64.7756 35.7339 67.3459 35.7339Z'
      fill='#1F86FF'
    />
  </svg>
);

const MODAL_ID = 'vacancy-generate';

const VacancyGenerateComponent = () => {
  const { mutate, isPending, isError } = useGenerateVacancy();
  const navigate = useNavigate();
  const form = useForm({
    initialValues: {
      vacancyName: '',
      generationRequest: '',
    },
    validate: zodResolver($VacationGenerateSchema),
  });

  const handleClose = () => {
    closeModal(MODAL_ID);
  };

  const handleSubmit = (values: typeof form.values) => {
    mutate(
      {
        vacancyName: values.vacancyName,
        generationRequest: values.generationRequest,
      },
      {
        onSuccess: (data: GenerateVacancyResponse) => {
          closeModal(MODAL_ID);
          navigate('/vacancy-list/create', {
            state: {
              ...INITIAL_VALUES,
              name: data.name,
              responsibilities: data.responsibilities,
              mandatoryRequirements: data.mandatoryRequirements,
              additionalRequirements: data.additionalRequirements,
              interviewFinalMessage: data.interviewFinalMessage,
              salaryFrom: data.salaryFrom,
              salaryTo: data.salaryTo,
              requiredCandidatesCount: String(data.requiredCandidatesCount),
              cityIds: data.city ? [String(data.city.id)] : [],
              currencyId: data.currency ? String(data.currency.id) : '',
              paymentPeriodId: data.paymentPeriod
                ? String(data.paymentPeriod.id)
                : '',
              employmentTypeIds: data.employmentTypes.map(e => String(e.id)),
              scheduleWorkIds: data.scheduleWorks.map(s => String(s.id)),
              activitySphereIds: data.activitySpheres.map(a => String(a.id)),
              skills: data.skills.map(s => s.name),
              activeFrom: data.vacancyActivities[0]?.activeFrom
                ? new Date(data.vacancyActivities[0].activeFrom)
                : undefined,
              activeTo: data.vacancyActivities[0]?.activeTo
                ? new Date(data.vacancyActivities[0].activeTo)
                : undefined,
            },
          });
        },
      }
    );
  };

  if (isPending) {
    return (
      <Stack gap={32} align='center' justify='center' h={228}>
        <SpinnerIcon />
        <Text size='md' c='tertiary.7' ta='center' w='80%'>
          Пожалуйста, подождите — формируем вакансию на основе вашего запроса
        </Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack gap={12}>
        <Stack gap={32} align='center' justify='center' h={174}>
          <ThemeIcon size='xl' bg='primary.1' radius={100}>
            <IconAlertTriangle color='var(--mantine-color-tertiary-4)' />
          </ThemeIcon>
          <Text size='md' c='tertiary.7' ta='center' w='80%'>
            Пожалуйста, подождите — формируем вакансию на основе вашего запроса
          </Text>
        </Stack>
        <Button w='100%' size='md' onClick={handleClose}>
          Закрыть
        </Button>
      </Stack>
    );
  }

  return (
    <Flex w='100%' flex={1}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex direction='column' gap={16} pb={16}>
          <TextInput
            w='100%'
            label='Название вакансии'
            description='Название должности, кого планируется нанимать. Максимальное количество символов - 250'
            placeholder='Введите название'
            labelProps={{
              style: { color: 'var(--mantine-color-primary-7)' },
            }}
            withAsterisk
            variant='filled'
            key={form.key('vacancyName')}
            {...form.getInputProps('vacancyName')}
            error={form.errors.vacancyName}
          />

          <Textarea
            label='Запрос на генерацию'
            description='Опишите вакансию простыми словами: кого ищете, какие задачи и условия. Синтия сгенерирует вакансию на основе вашего запроса. Максимальное количество символов - 3000'
            placeholder='Введите запрос на генерацию в свободной форме'
            withAsterisk
            variant='filled'
            styles={{
              input: { minHeight: 220 },
              label: { fontSize: 16, fontWeight: 600 },
            }}
            key={form.key('generationRequest')}
            {...form.getInputProps('generationRequest')}
            error={form.errors.generationRequest}
          />
        </Flex>

        <Flex gap={8} mt={12}>
          <Button variant='outline' size='md' onClick={handleClose}>
            Отмена
          </Button>
          <Button
            type='submit'
            size='md'
            disabled={
              !form.values.vacancyName || !form.values.generationRequest
            }
          >
            Сгенерировать
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};

export const useVacancyGenerateModal = () => {
  const openVacancyGenerateModal = () =>
    openModal({
      modalId: MODAL_ID,
      title: 'Генерация вакансии',
      size: MODAL_SIZE_MEDIUM,
      children: <VacancyGenerateComponent />,
    });
  return { openVacancyGenerateModal };
};
